import { useNavigate } from "react-router-dom";
import { useLiveKit } from "../../../hooks/useLiveKit";
import { useDeviceCheck } from "../../../hooks/useCameraCheck";
import { DeviceErrorPopup } from "../../../components/DeviceErrorPopup";
import CallHeader from "./CallHeader";
import ChatAndParticipents from "./ChatAndParticipents";
import VideoListComponent from "./VideoListComponent";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../context/ThemeProvider";
import { useWebsiteSettings } from "../../../store/api/useWebsiteSettings";
import { CallChromeProvider } from "./CallChromeContext";
import { useAppDispatch } from "../../../utils/hooks";
import { updatePeerData } from "../../../store/Slices/PeerJsSlice";
import { isCallTearingDown } from "../../../utils/endCallSession";

export default function VideoCall() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const { data: websiteSettings } = useWebsiteSettings();
  const [headerVisible, setHeaderVisible] = useState(true);
  const { cameraError, microphoneError, isChecking, checkDevices } =
    useDeviceCheck();

  const handleBack = () => {
    navigate(-1);
  };

  // LiveKit is the only media path. PeerJS hook is not mounted (legacy, to delete).
  const useLiveKitMode =
    typeof window === "undefined" ||
    sessionStorage.getItem("media_engine") !== "peerjs";
  const { unlockRoomAudio } = useLiveKit(useLiveKitMode);

  const call_type =
    typeof window !== "undefined"
      ? sessionStorage.getItem("call_type")
      : null;
  const isAudioCall = call_type === "audio";
  const callTypeLabel = isAudioCall ? "Audio call" : "Video call";

  useEffect(() => {
    if (isCallTearingDown()) return;
    const alreadyJoined =
      typeof window !== "undefined" &&
      sessionStorage.getItem("call_joined") === "true";
    const type = call_type === "audio" ? "audio" : "video";
    dispatch(
      updatePeerData({
        isVideoEnabled: type === "video",
        isAudioEnabled: true,
      }),
    );
    // Joined session already probed on dial / will gUM in LiveKit/PeerJS.
    if (alreadyJoined) return;
    void checkDevices(type);
  }, [checkDevices, call_type, dispatch]);

  useEffect(() => {
    const t = window.setTimeout(() => setHeaderVisible(false), 20_000);
    return () => window.clearTimeout(t);
  }, []);

  const logoSrc = useMemo(() => {
    if (!websiteSettings?.data) return null;
    return theme === "dark"
      ? websiteSettings.data.web_logo_dark ||
        websiteSettings.data.web_logo_light
      : websiteSettings.data.web_logo_light ||
        websiteSettings.data.web_logo_dark;
  }, [websiteSettings, theme]);

  const callChrome = useMemo(
    () => ({
      headerVisible,
      callTypeLabel,
      logoSrc,
    }),
    [headerVisible, callTypeLabel, logoSrc],
  );

  if (isChecking && !isCallTearingDown()) {
    return null;
  }

  if (microphoneError || (!isAudioCall && cameraError)) {
    return (
      <DeviceErrorPopup
        cameraError={isAudioCall ? null : cameraError}
        microphoneError={microphoneError}
        onBack={handleBack}
      />
    );
  }

  const isDark = theme === "dark";

  return (
    <CallChromeProvider value={callChrome}>
      <div
        className={`relative flex h-[100dvh] w-full flex-col overflow-hidden bg-gradient-to-br ${
          isDark
            ? "from-[#0b0b12] via-[#111222] to-[#0b0b12] text-white"
            : "from-[#f3f4f8] via-[#e9ecf3] to-[#f3f4f8] text-darkText"
        }`}
        onPointerDownCapture={() => {
          if (useLiveKitMode) void unlockRoomAudio();
        }}
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -top-40 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full blur-[140px] ${
            isDark ? "bg-themeColor/20" : "bg-themeColor/25"
          }`}
        />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -bottom-40 right-10 h-[32rem] w-[32rem] rounded-full blur-[140px] ${
            isDark ? "bg-indigo-500/15" : "bg-indigo-400/20"
          }`}
        />

        <CallHeader />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-6 sm:py-4 lg:px-10 lg:py-6 2xl:px-16 2xl:py-8">
          <div className="mx-auto flex min-h-0 w-full max-w-[2400px] flex-1 gap-6 2xl:gap-8">
            <VideoListComponent />
          </div>
        </div>

        <ChatAndParticipents />
      </div>
    </CallChromeProvider>
  );
}
